import {
  AfterViewChecked,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  QueryList,
  SimpleChanges,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { UnitOfMeasureOption } from '../../../../shared/services/sage-integration.model';
import {
  filterUnitGroupItems,
  hasOwnUnit,
  NamePart,
  nameParts,
  quantityNote,
  UNIT_GROUP_ALL_OWN_NOTE,
  UNIT_GROUP_PAGE_SIZE,
  unitGroupAllOwn,
  UnitGroupItem,
  unitGroupFieldLabel,
  unitGroupShownUomId,
  unitGroupStatus,
  unitGroupTitle,
  unitGroupUseLabel,
  UnitGroupView,
} from '../../../../shared/services/sage-unit-group.model';

@Component({
  selector: 'app-wizard-unit-group-card',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './wizard-unit-group-card.component.html',
  styleUrls: ['./sage-wizard.shared.css', './wizard-unit-group-card.component.css'],
})
export class WizardUnitGroupCardComponent implements OnChanges, AfterViewChecked {
  @ViewChild('groupSelect') private groupSelect?: ElementRef<HTMLSelectElement>;
  @ViewChildren('rowSelect') private rowSelects?: QueryList<ElementRef<HTMLSelectElement>>;

  @Input({ required: true }) group!: UnitGroupView;
  @Input() units: UnitOfMeasureOption[] = [];
  @Input() saving = false;

  @Output() groupUnitChange = new EventEmitter<number>();
  @Output() itemUnitChange = new EventEmitter<{ matchId: number; uomId: number }>();
  @Output() itemTypeChange = new EventEmitter<{ matchId: number; itemTypeId: number }>();

  readonly itemTypes = [
    { id: 1, label: 'Raw material' },
    { id: 2, label: 'Finished good' },
  ];

  open = false;
  query = '';
  limit = UNIT_GROUP_PAGE_SIZE;
  readonly allOwnNote = UNIT_GROUP_ALL_OWN_NOTE;
  /** A group pick was turned away because every item has its own unit. */
  allOwnShown = false;
  private selectsStale = true;

  // Puts the selects back after a failed save, as WizardUnitCardComponent does.
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['group'] || changes['units'] || (changes['saving'] && !this.saving)) this.selectsStale = true;
    if (changes['group']) this.allOwnShown = false;
  }

  ngAfterViewChecked(): void {
    if (!this.selectsStale) return;
    this.selectsStale = false;
    if (this.groupSelect) this.groupSelect.nativeElement.value = this.shownUomId ? String(this.shownUomId) : '';
    const byId = new Map(this.group.items.map((i) => [i.matchId, i]));
    this.rowSelects?.forEach((ref) => {
      const item = byId.get(Number(ref.nativeElement.dataset['matchId']));
      ref.nativeElement.value = item?.uomId ? String(item.uomId) : '';
    });
  }

  get count(): number {
    return this.group.items.length;
  }

  get title(): string {
    return unitGroupTitle(this.group);
  }

  get example(): UnitGroupItem | null {
    return this.group.kind === 'suggested' ? this.group.items[0] ?? null : null;
  }

  get fieldLabel(): string {
    return unitGroupFieldLabel(this.group);
  }

  get shownUomId(): number | null {
    return unitGroupShownUomId(this.group);
  }

  get useLabel(): string | null {
    return unitGroupUseLabel(this.group);
  }

  get status(): string | null {
    return unitGroupStatus(this.group);
  }

  get quantityNote(): string | null {
    return this.group.quantityExample ? quantityNote(this.group.quantityExample, 'first-sync') : null;
  }

  get filtered(): UnitGroupItem[] {
    return filterUnitGroupItems(this.group.items, this.query);
  }

  get shownRows(): UnitGroupItem[] {
    return this.filtered.slice(0, this.limit);
  }

  get moreCount(): number {
    return Math.max(0, Math.min(UNIT_GROUP_PAGE_SIZE, this.filtered.length - this.limit));
  }

  ownUnit(item: UnitGroupItem): boolean {
    return hasOwnUnit(this.group, item);
  }

  parts(item: UnitGroupItem): NamePart[] {
    return nameParts(item.name, item.hits);
  }

  toggle(): void {
    this.open = !this.open;
    this.limit = UNIT_GROUP_PAGE_SIZE;
    this.selectsStale = true;
  }

  onSearch(value: string): void {
    this.query = value;
    this.limit = UNIT_GROUP_PAGE_SIZE;
    this.selectsStale = true;
  }

  showMore(): void {
    this.limit += UNIT_GROUP_PAGE_SIZE;
    this.selectsStale = true;
  }

  use(): void {
    if (this.group.suggestedUnit) this.groupUnitChange.emit(this.group.suggestedUnit.id);
  }

  onGroupSelect(value: string): void {
    const id = Number(value);
    if (!id) return;
    // Nothing would be saved, so the picker goes back and the list the note points to opens.
    if (unitGroupAllOwn(this.group)) {
      this.allOwnShown = true;
      if (!this.open) this.toggle();
      this.selectsStale = true;
      return;
    }
    this.groupUnitChange.emit(id);
  }

  onRowSelect(item: UnitGroupItem, value: string): void {
    const id = Number(value);
    if (id) this.itemUnitChange.emit({ matchId: item.matchId, uomId: id });
  }

  chooseType(item: UnitGroupItem, itemTypeId: number): void {
    if (item.itemTypeId !== itemTypeId) this.itemTypeChange.emit({ matchId: item.matchId, itemTypeId });
  }

  trackItem(_: number, item: UnitGroupItem): number {
    return item.matchId;
  }
}
